<template>
  <b-container fluid class="bg-white shadow px-3 pb-5 px-6-lg pb-2-lg w-lg-70">
    <h2 class="py-3">Overview</h2>
    <b-alert show>
      <p>
        All parties on this notification need to have FOResT User accounts before you can
        finish this notification.
      </p>
      <p>
        You will need their FOResT User ID number and the last name or company name they
        entered for their account so you can add them to this notification.
      </p>
      <p>
        The landowner <strong>Or</strong> the designated agent should create a notification, <strong>not both</strong>.
      </p>
    </b-alert>
    <b-form>
      <ValidationObserver ref="observer" slim>
        <b-card>
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">My Notification Role</h5>
          </b-form-row>
          <b-form-row>
            <b-col cols="12" md="6">
              <label class="font-weight-bold" id="roleLabel">
                <span class="req-ind">*</span>Choose either Landowner or Designated Agent
              </label>
              <ValidationProvider name="Role" rules="roleRequired" v-slot="{ errors }">
                <b-form-radio-group
                  id="role"
                  name="role"
                  class="noselect"
                  v-model="overview.landowner"
                  aria-labelledby="roleLabel"
                  :aria-describedby="$errorsIdString(errors, 'role')"
                  :disabled="internalUser"
                >
                  <b-row no-gutters class="mb-2">
                    <b-col cols="11" lg="auto">
                      <b-form-radio id="landownerRole" name="landownerRole" :value="true">
                        <span class="sr-only">Choose either Landowner or Designated Agent</span>
                        I am the Landowner
                      </b-form-radio>
                    </b-col>
                    <b-col cols="1" lg="1">
                      <FontAwesomeIcon
                        focusable="true"
                        tabindex="0"
                        class="mx-2 text-primary align-self-center"
                        icon="info-circle"
                        id="lo-popover"
                      />
                      <b-popover
                        target="lo-popover"
                        :triggers="['hover', 'focus']"
                        placement="right"
                      >
                        A Landowner is a person, company, or other entity which holds title to
                        land, including joint ownership or tenants in common. Where the ownership
                        of the timber located on a parcel is different than the fee ownership of
                        the land, the owners of the timber are deemed to be a Landowner and are
                        jointly and severally responsible with the fee Landowner to comply with
                        this rule. Where a corporate Landowner is a wholly owned subsidiary of
                        another corporation, both parent and subsidiary are deemed to be the same
                        Landowner.
                      </b-popover>
                    </b-col>
                  </b-row>
                  <b-row no-gutters>
                    <b-col cols="11" lg="auto">
                      <b-form-radio
                        id="designatedAgentRole"
                        name="designatedAgentRole"
                        :value="false"
                      >
                        <span class="sr-only">Choose Either Landowner or Designated Agent</span>
                        I am the Landowner's Designated Agent
                      </b-form-radio>
                    </b-col>
                    <b-col cols="1">
                      <FontAwesomeIcon
                        focusable="true"
                        tabindex="0"
                        class="mx-2 text-primary align-self-center"
                        icon="info-circle"
                        id="da-popover"
                      />
                      <b-popover
                        target="da-popover"
                        :triggers="['hover', 'focus']"
                        placement="right"
                      >
                        A Designated Agent is a person, company or other entity that is authorized
                        by the Landowner to act on the Landowner's behalf for timber harvesting
                        and related activities on the Landowner's property.
                      </b-popover>
                    </b-col>
                  </b-row>
                  <InputErrors
                    :errors="errors"
                    errorsIdPrefix="role"
                    class="d-block invalid-feedback"
                  />
                </b-form-radio-group>
              </ValidationProvider>
            </b-col>
            <b-col cols="12" md="6">
              <span class="font-weight-bold">
                Choose additional roles
              </span>
              <b-row no-gutters class="mb-2">
                <b-col cols="11" lg="auto">
                  <b-form-checkbox
                    id="operatorRole"
                    name="operatorRole"
                    v-model="overview.operator"
                    class="d-inline-flex noselect mr-3"
                    :disabled="internalUser"
                  >
                    <span class="sr-only">Choose additional roles</span>
                    I am the Harvester/Operator
                  </b-form-checkbox>
                </b-col>
                <b-col cols="1">
                  <FontAwesomeIcon
                    focusable="true"
                    tabindex="0"
                    class="mx-2 text-primary align-self-center"
                    icon="info-circle"
                    id="operator-popover"
                  />
                  <b-popover
                    target="operator-popover"
                    :triggers="['hover', 'focus']"
                    placement="right"
                  >
                    A Harvester/Operator is a person, company, or other entity who either
                    performs or contracts harvests of forest products, construction of
                    land management roads, and/or installation of water crossings.
                  </b-popover>
                </b-col>
              </b-row>
              <b-row no-gutters>
                <b-form-checkbox
                  id="licensedForesterRole"
                  name="licensedForesterRole"
                  v-model="overview.licensedForester"
                  :disabled="mfsAdmin || !user.licensedForesterId"
                  class="d-inline-flex noselect mr-3"
                >
                  <span class="sr-only">Choose additional roles</span>
                  I am the Licensed Forester
                </b-form-checkbox>
                <small
                  id="licensedForesterInstructions"
                  name="licensedForesterInstructions"
                  class="text-muted pl-4"
                >
                  A Licensed Forester is a forester licensed under
                  <a
                    href="https://legislature.maine.gov/statutes/32/title32ch76sec0.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >32 M.R.S. c.76</a>.
                </small>
              </b-row>
              <b-row no-gutters v-if="!user.licensedForesterId">
                <small
                  id="licensedForesterNumber"
                  name="licensedForesterNumber"
                  class="text-muted" >
                  You do not have a valid license number associated to your FOResT Account
                  Profile. Please update your account if you are a Licensed Forester and will be
                  participating in that capacity for this Operation.
                </small>
              </b-row>
            </b-col>
          </b-form-row>
          <br>
          <b-form-row v-if="!overview.landowner && overview.licensedForester">
            <b-col>
              <label class="font-weight-bold" id="certMethodLabel" for="certMethod">
                <span class="req-ind">*</span>Choose the certification method for this FON
              </label>
              <ValidationProvider
                name="Certification Method"
                :rules="{certMethodRequired: true,
                  checkCertificationMethod: true}"
                v-slot="{ errors }">
                <b-form-radio-group
                  id="certMethod"
                  name="certificationMethodId"
                  class="noselect"
                  v-model="overview.certificationMethodId"
                  aria-labelledby="certMethodLabel"
                  :aria-describedby="$errorsIdString(errors, 'certMethod')"
                  :disabled="mfsAdmin">
                  <b-row no-gutters class="mb-2">
                    <b-col cols="11" lg="auto">
                      <b-form-radio :value="CERT_LANDOWNER">
                        {{certificationMethods[CERT_LANDOWNER]}}
                      </b-form-radio>
                    </b-col>
                    <b-col cols="1">
                      <FontAwesomeIcon
                        focusable="true"
                        tabindex="0"
                        class="mx-2 text-primary align-self-center"
                        icon="info-circle"
                        id="certByLandowner-popover"
                      />
                      <b-popover
                        target="certByLandowner-popover"
                        :triggers="['hover', 'focus']"
                        placement="right"
                      >
                        The account must be created before the FON can be completed.
                      </b-popover>
                    </b-col>
                  </b-row>
                  <b-row no-gutters class="mb-2">
                    <b-col cols="11">
                      <b-form-radio :value="CERT_LF_EMPLOY">
                        {{certificationMethods[CERT_LF_EMPLOY]}}
                      </b-form-radio>
                    </b-col>
                    <b-col cols="1">
                      <FontAwesomeIcon
                        focusable="true"
                        tabindex="0"
                        class="mx-2 text-primary align-self-center"
                        icon="info-circle"
                        id="certByEmployedLF-popover"
                      />
                      <b-popover
                        target="certByEmployedLF-popover"
                        :triggers="['hover', 'focus']"
                        placement="right"
                      >
                        The landowner must maintain with the Bureau a list of licensed
                        foresters authorized to sign for the landowner.
                      </b-popover>
                    </b-col>
                  </b-row>
                  <b-row no-gutters>
                    <b-col cols="11" mb->
                      <b-form-radio
                        :value="CERT_LF_FID"
                        aria-describedby="mfsEthicsLink">
                        {{certificationMethods[CERT_LF_FID]}}
                      </b-form-radio>
                    </b-col>
                    <b-col cols="1">
                      <FontAwesomeIcon
                        focusable="true"
                        tabindex="0"
                        class="mx-2 text-primary align-self-center"
                        icon="info-circle"
                        id="certByFiduciaryLF-popover"
                      />
                      <b-popover
                        target="certByFiduciaryLF-popover"
                        :triggers="['hover', 'focus']"
                        placement="right">
                        A “fiduciary responsibility” means that the Licensed Forester
                        represents the landowner and the landowner’s interest. The Licensed
                        Forester owes a duty of trust and full disclosure to the landowner
                        with regard to the harvesting operation.
                      </b-popover>
                    </b-col>
                  </b-row>
                  <small
                    v-if="overview.certificationMethodId == 3"
                    id="mfsEthicsLink"
                    class="text-muted pl-4">
                    This fiduciary responsibility is governed both by legal principles and by
                      <a
                        :href="links.mfsEthics"
                        target="_blank"
                        rel="noopener noreferrer">
                        Maine's Forester Code of Ethics
                      </a>.
                  </small>
                  <InputErrors
                    :errors="errors"
                    errorsIdPrefix="certMethod"
                    class="d-block invalid-feedback" />
                </b-form-radio-group>
              </ValidationProvider>
              <b-alert show class="my-3 px-3">
                <p>
                  Landowners who are able to create FOResT accounts, especially those who may
                  submit multiple FONs/harvest more than once, are strongly encouraged to create
                  a FOResT account.
                </p>
                <p>
                  This will allow the Landowner to access their FON information independently
                  and avoid repeated entry and duplication of landowner information within the
                  FOResT system.
                </p>
              </b-alert>
            </b-col>
          </b-form-row>
        </b-card>
        <b-card v-if="overview.landowner !== null">
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">Operation Schedule</h5>
          </b-form-row>
          <p>
            <label>When will your Operation occur?</label>
          </p>
          <b-row>
            <b-col cols="12" md="6">
              <ValidationProvider
                name="Operation Start Date"
                rules="required"
                v-slot="{ errors, classes }"
                slim
              >
                <b-form-group
                  label-cols="12"
                  label-cols-lg="4"
                  content-cols-lg="8"
                  label-for="operationStartDate">
                  <template v-slot:label>
                    <span class="req-ind">*</span>Operation Start Date
                  </template>
                  <b-form-datepicker
                    id="operationStartDate"
                    name="operationStartDate"
                    :class="classes"
                    v-model="overview.operationStartDate"
                    ref="datepicker"
                    placeholder="Choose a date"
                    local="en"
                    :aria-describedby="$errorsIdString(errors, 'operationStartDate')"
                    :disabled="mfsAdmin"
                  >
                    <template #button-content>
                      <FontAwesomeIcon icon="calendar-alt" aria-hidden="true" />
                      <span class="sr-only">Choose a date</span>
                    </template>
                  </b-form-datepicker>
                  <InputErrors
                    :errors="errors"
                    errorsIdPrefix="operationStartDate"
                    class="d-block invalid-feedback"
                  />
                </b-form-group>
              </ValidationProvider>
                  <div v-if="isStartDateInFuture" style="font-size: 0.8em;">The number for this notification will not display for you before this start date.
                  If you need a Notification Number sooner, pick a start date that begins sooner for this notification.</div>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group
                label-for="operationEndDate"
                label-cols="12"
                label-cols-lg="4"
                label-class="font-weight-bold"
                content-cols-lg="8">
                <template v-slot:label>
                  Operation End Date
                </template>
                <b-input id="operationEndDate" disabled :value="this.operationEndDate | dateString">
                  {{this.operationEndDate | dateString}}
                </b-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-modal ref="cancelConfirmOperationStartDateModal" id="cancelConfirmOperationStartDateModal" centered hide-header-close size="md" :title="`Confirm Operation Start Date`">
          <p>Are you sure '{{operationStartDateTimeString}}' is correct?</p>
          <div slot="modal-footer" class="d-flex full-width">
            <b-button pill variant="danger" class="mr-3" @click="onOperationStartDateNo()" aria-label="No, change the Operation Start Date">
              <span>No</span>
            </b-button>
            <b-button pill variant="primary" @click="onOperationStartDateYes()" aria-label="Yes, confirm the Operation Start Date">
              <span>Yes</span>
            </b-button>
          </div>
        </b-modal>

        <b-card v-if="overview.landowner !== null">
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">Property Details</h5>
          </b-form-row>
          <b-row>
            <b-col cols="12" md="9">
              <ValidationProvider
                name="Town"
                rules="required"
                v-slot="{ errors, classes }"
              >
                <b-form-group
                  label-cols="12"
                  label-cols-lg="4"
                  content-cols-lg="8"
                  label-for="town"
                >
                  <template v-slot:label>
                    <span class="req-ind">*</span>Town, Township, or Plantation
                  </template>
                  <select
                    id="town"
                    name="town"
                    v-model="overview.townGeocodeNum"
                    class="form-control justify-content-end"
                    @change="onTownChange($event)"
                    :class="classes"
                    :aria-describedby="$errorsIdString(errors, 'town')"
                    :disabled="mfsAdmin"
                  >
                    <option :value="null" disabled>Select a Town</option>
                    <option
                      v-for="option in townOptions"
                      :key="option.geocodeNum"
                      :value="option.geocodeNum"
                    >
                      {{option.name}}
                    </option>
                  </select>
                  <InputErrors :errors="errors" errorsIdPrefix="town" />
                </b-form-group>
              </ValidationProvider>
            </b-col>
            <b-col cols="12" md="3">
              <div class="col-form-label">
                <span class="font-weight-bold">County:</span>&nbsp;
                {{getProperty(overview.townGeocodeNum, 'countyName')}}
              </div>
            </b-col>
          </b-row>
          <ValidationProvider
            name="Road Name"
            rules="required|min:3"
            v-slot="{ errors, classes }"
            slim
          >
            <b-form-group
              label-cols="12"
              label-cols-lg="6"
              content-cols-lg="6"
              label-for="roadName">
              <template v-slot:label>
                <span class="req-ind">*</span>Name of nearest public or private all-weather
                road to access woodlot
              </template>
              <b-form-input
                id="roadName"
                name="roadName"
                v-model="overview.roadName"
                placeholder="Enter Road Name"
                maxlength="75"
                :class="classes"
                :aria-describedby="$errorsIdString(errors, 'roadName')"
                :disabled="mfsAdmin"
              />
              <InputErrors :errors="errors" errorsIdPrefix="roadName" />
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            name="Property Parcel Acreage"
            rules="required|max_decimal_singular:1|min_value:0.1"
            v-slot="{ errors, classes }"
            slim
          >
            <b-form-group
              label-cols="12"
              label-cols-lg="6"
              content-cols-lg="6"
              label-for="parcelAcreage">
              <template v-slot:label>
                <span class="req-ind">*</span>Property Parcel Acreage
              </template>
              <b-form-input
                type="number"
                id="parcelAcreage"
                name="parcelAcreage"
                v-model="overview.parcelAcreage"
                placeholder="Enter Property Parcel Acreage"
                :class="classes"
                maxlength="7"
                :aria-describedby="$errorsIdString(errors, 'parcelAcreage')"
                :disabled="mfsAdmin"
              />
              <InputErrors :errors="errors" errorsIdPrefix="parcelAcreage" />
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            name="Parcel purchased within 5 years"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <b-form-group
              label-cols="12"
              label-cols-lg="8"
              label-class="font-weight-bold"
              content-cols-lg="4">
              <template v-slot:label>
                <div id="purchasedLabel">
                  <span class="req-ind">*</span>Was this parcel purchased within the past five
                  years?
                </div>
              </template>
              <b-form-radio-group
                id="purchased"
                name="purchased"
                v-model="overview.purchased"
                aria-labelledby="purchasedLabel"
                :aria-describedby="$errorsIdString(errors, 'purchased')"
                :disabled="mfsAdmin"
              >
                <b-form-radio class="noselect" :value="true">
                  Yes
                </b-form-radio>
                <b-form-radio class="noselect" :value="false">
                  No
                </b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="purchased"
                class="d-block invalid-feedback text-right"
              />
            </b-form-group>
          </ValidationProvider>
        </b-card>
      </ValidationObserver>
    </b-form>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import LookupModules from '@/store/moduleConfig';
import FormSubmit from '@/mixins/FormSubmit';
import CertMethods from '@/mixins/CertificationMethods';

export default {
  name: 'OverviewForm',
  mixins: [FormSubmit, CertMethods],
  props: ['notification'],
  data() {
    const overview = {
      landowner: null,
      designatedAgent: false,
      licensedForester: false,
      certificationMethodId: null,
      operationStartDate: null,
      roadName: '',
      parcelAcreage: null,
      purchased: null,
      townGeocodeNum: null,
    };
    let isLUPCTown;
    if (this.notification) Object.assign(overview, this.notification);
    return {
      overview,
      isLUPCTown,
      operationStartDateOld: null,
      operationStartDateWatchEnabled: true,
      loadedTownGeocodeNum: null, // Store loaded town selection
    };
  },
  created() {
    this.$store.dispatch(`${LookupModules.TOWNS.namespace}/fetchOptions`);
    // Initialize loaded town value
    this.loadedTownGeocodeNum = this.overview.townGeocodeNum;
  },
  computed: {
    ...mapState('Notification', {
      roleError: state => state.roleError,
    }),
    ...mapState('Configuration', {
      links: state => state.links,
    }),
    ...mapState(LookupModules.TOWNS.namespace, {
      townOptions: state => state.options,
      townsLoading: state => state.loading,
    }),
    ...mapState('User', {
      user: state => state.user,
    }),
    ...mapGetters(LookupModules.TOWNS.namespace, ['getProperty']),
    ...mapGetters('Notification', ['isLOManaged']),
    ...mapGetters('User', ['mfsAdmin', 'mfsUser', 'tceoUser', 'infwbUser']),
    operationStartDateTimeString() {
      const result = new Date(this.overview.operationStartDate).toLocaleString(
        'en-US',
        {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        },
      );
      return result;
    },
    operationEndDate() {
      return this.overview.operationStartDate ? moment(this.overview.operationStartDate).add(2, 'years') : null;
    },
    isStartDateInFuture() {
      if (!this.overview.operationStartDate) return false;
      return moment(this.overview.operationStartDate).isAfter(moment(), 'day');
    },
    internalUser() {
      return this.mfsAdmin || this.mfsUser || this.tceoUser || this.infwbUser;
    },
  },
  watch: {
    // Unset the certification method whenever the user unchecks licensedForester additional role
    // eslint-disable-next-line object-shorthand
    'overview.licensedForester'(isLF) {
      if (!isLF) {
        this.overview.certificationMethodId = null;
      }
    },
    // Unset the certification method whenever the user sets themselves as the landowner
    // eslint-disable-next-line object-shorthand
    'overview.landowner'(isLO) {
      if (isLO) {
        this.overview.certificationMethodId = null;
      }
    },
    // eslint-disable-next-line object-shorthand
    'overview.operationStartDate'(newValue, oldValue) {
      this.operationStartDateOld = oldValue;
      if (this.operationStartDateWatchEnabled) {
        const currentDateMilliseconds = new Date().getTime();
        const newOperationStartDateMilliseconds = new Date(newValue).getTime();
        const milliseconds = newOperationStartDateMilliseconds - currentDateMilliseconds;
        const days = milliseconds / (1000 * 3600 * 24);
        if (days >= 180) {
          this.$refs.cancelConfirmOperationStartDateModal.show();
        }
      }
      this.operationStartDateWatchEnabled = true;
    },
  },
  methods: {
    onOperationStartDateNo() {
      this.$refs.cancelConfirmOperationStartDateModal.hide();
      this.operationStartDateWatchEnabled = false;
      this.overview.operationStartDate = this.operationStartDateOld;
      this.$refs.datepicker.focus();
    },
    onOperationStartDateYes() {
      this.$refs.cancelConfirmOperationStartDateModal.hide();
    },
    onTownChange($event) {
      this.isLUPCTown = !!$event.target.value.isLUPCTown;
    },
    async submitWrapper(source) {
      // bypass if admin
      if (this.mfsAdmin) {
        return true;
      }
      if (typeof this.overview.operationStartDate === 'string' && this.overview.operationStartDate !== null) {
        try {
          this.operationStartDateWatchEnabled = false;
          this.overview.operationStartDate = new Date(`${this.overview.operationStartDate}T00:00:00`);
        } catch (exception) {
          console.error(exception);
        }
      }
      if (this.loadedTownGeocodeNum !== this.overview.townGeocodeNum) { // in case save draft button is clicked
        this.overview.lupcModal = false;
        this.notification.lupcModal = false;
        this.loadedTownGeocodeNum = this.overview.townGeocodeNum;
      } else {
        this.overview.lupcModal = this.notification.lupcModal;
      }
      this.overview.lupcModal = this.notification.lupcModal;
      if (this.overview.operationStartDate != null) {
        if (this.overview.operationStartDate.toLocaleString() === 'Invalid Date') {
          this.overview.operationStartDate = this.notification.operationStartDate;
        }
      }

      return this.submit(
        this.$refs.observer,
        this.$route.params.id ? 'Notification/saveDraft' : 'Notification/createDraft',
        this.overview,
        source,
      );
    },
    async submitSuccess(id) {
      if (!this.$route.params.id) await this.$router.push({ name: 'notification', params: { id } });
    },
  },
};
</script>
<style>
.btn-layer-control {
  font-size: 14px;
  height: 40px;
  margin-right: 4px;
}
</style>
